

import { Button } from "@/shared/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import {
    Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Switch } from "@/shared/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import { Craftsman, CraftsmanInsert, CraftsmanUpdate } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

// 職人種別の選択肢（仮のデータ、実際はAPIから取得）
const CRAFTSMAN_TYPES = [
    { id: null, label: "選択してください" },
    { id: "11111111-1111-1111-1111-111111111111", label: "型枠大工" },
    { id: "22222222-2222-2222-2222-222222222222", label: "鉄筋工" },
    { id: "33333333-3333-3333-3333-333333333333", label: "左官工" },
    { id: "44444444-4444-4444-4444-444444444444", label: "電気工" },
    { id: "55555555-5555-5555-5555-555555555555", label: "配管工" },
];

// バリデーションスキーマ定義
const formSchema = z.object({
    name: z.string().trim().min(1, { message: "氏名は必須です。" }),
    daily_rate: z.coerce.number({
        message: "日単価は数値で入力してください。"
    }).int().positive({ message: "日単価は0より大きい値を入力してください。" }),
    craftsman_type_id: z.string().nullable().optional(),
    is_active: z.boolean().optional().default(true),
});

type CraftsmanFormData = Omit<Craftsman, 'id' | 'created_at' | 'updated_at'> & { daily_rate: number | null };

/**
 * 職人フォームダイアログのprops
 * @param isOpen ダイアログが開いているかどうか
 * @param onClose ダイアログを閉じる
 * @param onSubmit フォームの送信処理
 * @param defaultValues デフォルト値
 * @param isUpdate 更新かどうか
 */
interface CraftsmanFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CraftsmanInsert | CraftsmanUpdate) => Promise<boolean>;
    defaultValues?: Craftsman | null;
    isUpdate: boolean;
}

/**
 * 職人フォームダイアログ
 * @param isOpen ダイアログが開いているかどうか
 * @param onClose ダイアログを閉じる
 * @param onSubmit フォームの送信処理
 * @param defaultValues デフォルト値
 * @param isUpdate 更新かどうか
 */
export default function CraftsmanFormDialog({ isOpen, onClose, onSubmit, defaultValues, isUpdate }: CraftsmanFormDialogProps) {
    const form = useForm<CraftsmanFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: defaultValues?.name ?? '職人名',
            daily_rate: defaultValues?.daily_rate ?? 10000,
            craftsman_type_id: defaultValues?.craftsman_type_id ?? null,
            is_active: defaultValues?.is_active ?? true,
        },
    });

    React.useEffect(() => {
        if (isOpen) {
            form.reset({
                name: defaultValues?.name ?? '職人名',
                daily_rate: defaultValues?.daily_rate ?? 10000,
                craftsman_type_id: defaultValues?.craftsman_type_id ?? null,
                is_active: defaultValues?.is_active ?? true,
            });
        }
    }, [isOpen, defaultValues, form]);


    /**
     * フォームの送信処理
     * @param values フォームの値
     */
    const handleFormSubmit = async (values: CraftsmanFormData) => {
        const dataToSubmit = {
            ...defaultValues,
            ...values,
        } as CraftsmanInsert | CraftsmanUpdate;

        const success = await onSubmit(dataToSubmit);
        if (success) {
            onClose();
        }
    };

    /**
     * 職人フォームダイアログの表示
     * @returns 職人フォームダイアログ
     */
    return (
        <Dialog open={isOpen} onOpenChange={(open) => {
            if (!open) {
                form.reset();
                onClose();
            }
        }}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isUpdate ? '職人情報の編集' : '新規職人の追加'}</DialogTitle>
                    <DialogDescription>
                        {isUpdate ? '職人の情報を更新します。' : '新しい職人の情報を入力してください。'}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>氏名 <span className="text-destructive">*</span></FormLabel>
                                    <FormControl>
                                        <Input placeholder="山田 太郎" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="daily_rate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>日単価 (円) <span className="text-destructive">*</span></FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            placeholder="例: 20000"
                                            {...field}
                                            value={field.value === null || field.value === undefined ? '' : String(field.value)}
                                            onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        日雇いの単価を入力してください。
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="craftsman_type_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>職人種別</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(value === 'none' ? null : value)} value={field.value ?? 'none'}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="職人種別を選択してください" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {CRAFTSMAN_TYPES.map((type) => (
                                                <SelectItem key={type.id || 'none'} value={type.id || 'none'}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {isUpdate && (
                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                        <div className="space-y-0.5">
                                            <FormLabel>在籍状態</FormLabel>
                                            <FormDescription>
                                                この職人が現在在籍しているかを示します。
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value ?? true}
                                                onCheckedChange={field.onChange}
                                                aria-label="在籍状態"
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => {
                                form.reset();
                                onClose();
                            }}>キャンセル</Button>
                            <Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isValid}>
                                {form.formState.isSubmitting ? '保存中...' : (isUpdate ? '更新' : '追加')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
} 